/**
 * Action limit (quota) fetch/update helpers for user, project, stream, or team scopes.
 *
 * Where: `ActionLimitsControl` and settings surfaces that edit LinkedIn automation caps.
 * Uses: `apiClient`.
 * Contract: GET/POST `/action-limitations/` with params `scope`, `resource_uuid`, `time_window`; POST `/action-limitations/clear-blockage/`, `/action-limitations/reset-to-automatic/`; all methods return `response.data.output` as `ActionLimitsOutput`.
 * Behavior: `getLimits` reads caps/usage; `updateLimits` posts full item set; blockage clear and reset are separate POSTs.
 * Invariants: meaning of `resourceId` depends on `scope` (see `FetchActionLimitsParams` JSDoc).
 */
import { apiClient } from './client';

export type LimitScope = 'user' | 'project' | 'stream' | 'team';

export type TimeWindow = 'MINUTE' | 'HOUR' | 'DAY' | 'WEEK' | 'MONTH';

export interface ActionLimitItem {
  action: string;
  label: string;
  time_window: TimeWindow;
  minimum: number;
  default_value: number | null;
  maximum: number | null;
  current_value: number | null;
  used: number | null; // null when not configured
  is_user_set: boolean;
  blocked_until?: string | null;
  blocking_reason?: string | null;
}

export interface ActionLimitsOutput {
  items: ActionLimitItem[];
}

export interface ActionLimitsResponse {
  output: ActionLimitsOutput;
  error: boolean;
  http_response_code: number;
  error_messages: string[];
}

export interface FetchActionLimitsParams {
  scope: LimitScope;
  /**
   * Resource identifier:
   * - project: project UUID
   * - stream: stream UUID
   * - user: optional (backend infers from current user)
   */
  resourceId?: string;
  timeWindow: TimeWindow;
}

export interface UpdateLimitItemInput {
  action: string;
  time_window: TimeWindow;
  maximum: number | null;
}

export interface UpdateActionLimitsParams {
  scope: LimitScope;
  resourceId?: string;
  timeWindow: TimeWindow;
  items: UpdateLimitItemInput[];
}

export interface ClearBlockageParams {
  scope: LimitScope;
  resourceId?: string;
  action: string;
  timeWindow: TimeWindow;
}

export interface ResetToAutomaticParams {
  scope: LimitScope;
  resourceId?: string;
  action: string;
  timeWindow: TimeWindow;
}

const BASE_URL = '/action-limitations/';

export const actionLimitsApi = {
  getLimits: async (params: FetchActionLimitsParams): Promise<ActionLimitsOutput> => {
    const { scope, resourceId, timeWindow } = params;

    const response = await apiClient.get<ActionLimitsResponse>(BASE_URL, {
      params: {
        scope,
        resource_uuid: resourceId,
        time_window: timeWindow,
      },
    });

    return response.data.output;
  },

  updateLimits: async (params: UpdateActionLimitsParams): Promise<ActionLimitsOutput> => {
    const { scope, resourceId, timeWindow, items } = params;

    const response = await apiClient.post<ActionLimitsResponse>(BASE_URL, {
      scope,
      resource_uuid: resourceId,
      time_window: timeWindow,
      items,
    });

    return response.data.output;
  },

  clearBlockage: async (params: ClearBlockageParams): Promise<ActionLimitsOutput> => {
    const { scope, resourceId, action, timeWindow } = params;

    const response = await apiClient.post<ActionLimitsResponse>(
      `${BASE_URL}clear-blockage/`,
      {
        scope,
        resource_uuid: resourceId,
        action,
        time_window: timeWindow,
      },
    );

    return response.data.output;
  },

  resetToAutomatic: async (params: ResetToAutomaticParams): Promise<ActionLimitsOutput> => {
    const { scope, resourceId, action, timeWindow } = params;

    const response = await apiClient.post<ActionLimitsResponse>(
      `${BASE_URL}reset-to-automatic/`,
      {
        scope,
        resource_uuid: resourceId,
        action,
        time_window: timeWindow,
      },
    );

    return response.data.output;
  },
};


